export const copy = {
  nav: { contact: "Contact" },
  brand: {
    nameLead: "Explor",
    nameAccent: "dio",
    tagline: ["Explore", "Play", "Manage"],
  },
  hero: {
    titleLead: "Your files. Your media.",
    titleAccent: "Your way.",
    subtitle: "A powerful all-in-one app for files, media and more.",
  },
  stores: {
    prefix: "Coming Soon on",
    google: "Google Play",
    apple: "the App Store",
  },
  library: {
    title: "One Media Library. Any Storage Source.",
    description: "Bring your music, videos, audiobooks and more together — whether they’re on your phone, SD card, NAS, SMB or FTP.",
    action: "Learn More",
    modalDescription: "Bring all your media together, no matter where it’s stored.",
    modalBody: "Explordio lets you build a single media library from the storage you already use. Access music, videos, audiobooks, and other media without having to move everything into one location.",
    storageHeading: "Storage sources",
    storageSources: ["Phone / Internal Storage", "SD Card", "NAS", "SMB / Samba", "FTP"],
    actionsHeading: "What you can do",
    actions: [
      "Build one media library across multiple storage sources",
      "Browse and manage files directly from network storage",
      "Stream music and video without moving files",
      "Access media from NAS, SMB/Samba, and FTP",
      "Use your existing folder structure and storage",
      "Play media from local or network locations",
      "Switch between storage sources without leaving Explordio",
    ],
    modalTagline: "One library. Every place your media lives.",
  },
  contact: {
    title: "Contact Explordio",
    subtitle: "Send a message to the Explordio team.",
    name: "Name",
    email: "Email",
    phone: "Phone (optional)",
    subject: "Subject (optional)",
    comment: "Comment",
    send: "Send Message",
    sending: "Sending…",
    successTitle: "Message sent",
    successMessage: "Thanks for reaching out. The Explordio team will get back to you.",
  },
};

export const features = [
  {
    id: "file-management",
    title: "File Management",
    description: "Powerful tools for all your files",
    icon: "folder",
    modalDescription: "Powerful file management, built for the way you use your devices.",
    modalHeading: "Key features",
    modalFeatures: [
      "Selectable single or dual-pane file manager",
      "Browse Local storage & SD card",
      "Browse NAS, SMB/Samba & FTP",
      "Copy, move, rename & delete",
      "Multi-select & batch operations",
      "Batch rename",
      "Pin folders / locations",
      "ZIP / unzip",
      "Duplicate file finder",
      "Storage & disk analyzer",
      "File properties & search function",
      "Undo / redo operations",
      "Job Center for transfers and operations",
      "Backup tools",
      "Open files from other apps",
    ],
    modalTagline: "Manage your files your way, wherever they’re stored.",
  },
  {
    id: "music-audio",
    title: "Music & Audio",
    description: "Your music, radio, podcasts and more",
    icon: "music",
    modalDescription: "A complete music experience for your local and network audio.",
    modalHeading: "Key features",
    modalFeatures: [
      "Music library over any supported storage source",
      "Browse by artists, albums, & songs",
      "Playlists",
      "Album artwork & automatic artwork retrieval",
      "Equalizer",
      "Sleep timer",
      "Queue management",
      "Favorites",
      "Audiobook library & playback",
      "Audiobook resume position",
      "Podcast playback",
      "Internet radio",
      "Voice-controlled music playback",
      "Voice-controlled radio discovery",
      "Local & network audio streaming",
      "Car controls & media controls",
      "Convert audio to a wide range of formats",
    ],
    modalTagline: "Your music. Your audiobooks. Your radio. All in one place.",
  },
  {
    id: "video-player",
    title: "Video Player",
    description: "A powerful video experience",
    icon: "video",
    modalDescription: "A powerful video player for local files and network streaming.",
    modalHeading: "Key features",
    modalFeatures: [
      "Built-in video player",
      "Local & network video playback",
      "NAS, SMB/Samba & FTP streaming",
      "Subtitles",
      "Chapters",
      "Repeat & A/B repeat",
      "Playback speed control",
      "Resume playback",
      "Track selection",
      "Full-screen playback",
      "Watched & completed video tracking",
      "Convert video to a wide range of formats",
    ],
    modalTagline: "Play your videos your way, wherever they're stored.",
  },
  {
    id: "image-studio",
    title: "Image Studio",
    description: "View, edit, enhance and restore",
    icon: "image",
    modalDescription: "View, edit, enhance, and organize your images.",
    modalHeading: "Key features",
    modalFeatures: [
      "Full-featured image viewer",
      "Pan & pinch-to-zoom",
      "Crop & rotate",
      "Markup & annotation tools",
      "Image effects & adjustments",
      "Image restoration tools",
      "Set images as wallpaper",
      "Share images",
      "Browse images from local and network storage",
      "Support for an impressive range of image formats",
      "Convert images to a wide range of formats",
    ],
    modalTagline: "View, edit, and enhance your images.",
  },
  {
    id: "documents-reading",
    title: "Documents & Reading",
    description: "PDF, Office, EPUB and more",
    icon: "document",
    modalDescription: "Read, view, and work with documents in one place.",
    modalHeading: "Key features",
    modalFeatures: [
      "PDF viewer",
      "EPUB reader",
      "View Microsoft Office documents",
      "Text file viewer",
      "Web archive viewer",
      "Open documents from NAS, SMB/Samba & FTP",
      "Support for a wide range of document formats",
    ],
    modalTagline: "Read more. Open more. Wherever your files are stored.",
  },
  {
    id: "network-nas",
    title: "Network & NAS",
    description: "Access your files anywhere",
    icon: "network",
    modalDescription: "Connect to your network storage and manage your files from anywhere.",
    modalHeading: "Key features",
    modalFeatures: [
      "NAS & network storage",
      "Samba / SMB 3 support",
      "FTP",
      "Browse and manage remote files",
      "Stream music & video directly from network storage",
      "Copy, move, rename & delete remote files",
      "Network file transfers",
      "Remote library playback",
      "Backup to network storage",
      "VPN-compatible remote access",
    ],
    modalTagline: "Your network storage, right at your fingertips.",
  },
  {
    id: "voice-discovery",
    title: "Voice & Discovery",
    description: "Talk to Explordio",
    icon: "voice",
    modalDescription: "Find and control your media with natural voice commands.",
    modalHeading: "Key features",
    modalFeatures: [
      "Voice-controlled music playback",
      "Voice-controlled radio discovery",
      "Search radio by station, genre, country, city & language",
      "Natural-language radio searches",
      "Voice-controlled playback commands",
      "Conversational radio search & refinement",
      "Free audiobook discovery",
      "Podcasts & Internet Radio",
      "Available in 14 languages",
    ],
    modalTagline: "Just say what you want to hear.",
  },
  {
    id: "built-for-car",
    title: "Built for Your Car",
    description: "Android Auto, CarPlay and more",
    icon: "car",
    modalDescription: "Take your music and media experience on the road.",
    modalHeading: "Key features",
    modalFeatures: [
      "CarPlay support",
      "Android Auto support",
      "Steering-wheel media controls",
      "Car display integration",
      "Play, pause, next & previous controls",
      "Music playback from your Explordio library",
      "Internet radio / podcast playback",
      "Audiobook playback",
    ],
    modalTagline: "Your media, wherever the road takes you.",
  },
];

export const sources = ["Phone", "SD Card", "NAS", "SMB", "FTP"];